import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { useSession } from '@marriott/mi-headless-utils';
import { Button, Heading, Image, headingType, size, tags, useWindowSize } from '@marriott/mi-ui-library';
import { convertToCommaSeparatedNumber } from '@marriott/shared/mi-helper-utils';

import { MerchandisingCoBrandCardProps } from './MerchandisingCoBrandCard.types';
import { getTrackingPropertiesDynamic, returnDecodedString } from '../../utils/helpers';
import { MarkDownText } from '../../molecules/markDown/MarkDown';

import { StyledMerchandisingCoBrandCard } from './MerchandisingCoBrandCard.styles';

export const MerchandisingCoBrandCardComponent: FC<MerchandisingCoBrandCardProps> = props => {
  const resolvedMetaData = props.metaData ?? props.metadata ?? {};
  const {
    mboxParameter,
    trackingProperties = {},
    image,
    headerLong = '',
    headerMedium = '',
    headerShort = '',
    descriptionLong = '',
    descriptionMedium = '',
    descriptionShort = '',
    callToActionText1 = '',
    destinationUrl1 = '',
    openInNewTab = false,
    openInNewWindowAriaLabel = '',
    styleClass = '',
    legalLong = '',
    mathLabel1 = '',
    mathLabel2 = '',
    mathLabel3 = '',
    mathTitle = '',
    mathValue = '',
    mathType = '',
    isTargetContentLoaded = mboxParameter && !AuthoringUtils.isInEditor() ? false : true,
    isTargetContentError = false,
    isSquare = false,
    displayEvergreenContent,
    isHide = false
  } = props;

  const { datalayer } = useSession();
  const buttonRef = useRef(null) as unknown as React.MutableRefObject<HTMLInputElement>;
  const [variationDetails, setVariationsDetails] = useState({
    isVariationsAvailable: false,
    isPointsVariation: false,
    isCurrencyVariation: false,
    isNightsVariation: false,
  });
  const { width = 0 } = useWindowSize();
  const isTablet = width >= 768 && width < 992;
  const isSquareCard = (isTablet ? true : false) || isSquare;
  const title = returnDecodedString(headerLong || headerMedium || headerShort);
  const description = returnDecodedString(descriptionLong || descriptionMedium || descriptionShort);

  const variationProps = useMemo(
    () => ({
      isPointsVariation: variationDetails.isPointsVariation,
      isCurrencyVariation: variationDetails.isCurrencyVariation,
      isNightsVariation: variationDetails.isNightsVariation,
      currencyType: datalayer?.['prop_currency_type'] as string,
      currencyTotalStayAmount: parseFloat(`${datalayer?.['res_revenue_total_locale'] ?? '0'}`),
      pointsTotalStayAmount: parseInt(`${datalayer?.['mr_prof_points_balance'] ?? '0'}`, 10),
      currentNights: parseInt(`${datalayer?.['mr_prof_nights_booked_this_year'] ?? '0'}`, 10),
      mathLabel1,
      mathLabel2,
      mathLabel3,
      mathValue,
      mathTitle,
    }),
    [
      variationDetails.isPointsVariation,
      variationDetails.isCurrencyVariation,
      variationDetails.isNightsVariation,
      datalayer?.['prop_currency_type'],
      datalayer?.['res_revenue_total_locale'],
      datalayer?.['mr_prof_points_balance'],
      datalayer?.['mr_prof_nights_booked_this_year'],
      mathLabel1,
      mathLabel2,
      mathLabel3,
      mathValue,
      mathTitle,
    ]
  );
  const [value1, setValue1] = useState('');
  const [value2, setvalue2] = useState('');
  const [value3, setValue3] = useState('');

  useEffect(() => {
    if (
      isTargetContentLoaded &&
      ((mathType?.toLowerCase() === 'currency' && !!datalayer?.['prop_currency_type']) ||
        mathType?.toLowerCase() === 'points' ||
        mathType?.toLowerCase() === 'nights')
    ) {
      setVariationsDetails({
        isVariationsAvailable: true,
        isPointsVariation: mathType?.toLowerCase() === 'points',
        isCurrencyVariation: mathType?.toLowerCase() === 'currency' && !!datalayer?.['prop_currency_type'],
        isNightsVariation: mathType?.toLowerCase() === 'nights',
      });
    } else {
      /* When mathType is not available reset variationsDetails to default */
      setVariationsDetails({
        isVariationsAvailable: false,
        isPointsVariation: false,
        isCurrencyVariation: false,
        isNightsVariation: false,
      });
    }
  }, [isTargetContentLoaded, mathType, datalayer]);

  useEffect(() => {
    switch (true) {
      case variationProps.isPointsVariation: {
        const yourPointsbalance = convertToCommaSeparatedNumber(variationProps.pointsTotalStayAmount);
        const bonusPoints = variationProps.mathValue;
        const totalPointsBalance =
          variationProps.pointsTotalStayAmount +
          (variationProps.mathValue ? parseInt(variationProps.mathValue.replace(/,/g, ''), 10) : 0);
        setValue1(yourPointsbalance);
        setvalue2(bonusPoints);
        setValue3(convertToCommaSeparatedNumber(totalPointsBalance));
        break;
      }
      case variationProps.isCurrencyVariation: {
        const totalStay =
          convertToCommaSeparatedNumber(variationProps.currencyTotalStayAmount) + ' ' + variationProps.currencyType;
        const statementCredit = variationProps.mathValue + ' ' + variationProps.currencyType;
        const totalAmount =
          variationProps.currencyTotalStayAmount +
          (variationProps.mathValue ? parseFloat(variationProps.mathValue.replace(/,/g, '')) : 0);
        const totalAmountInCurrency = convertToCommaSeparatedNumber(totalAmount) + ' ' + variationProps.currencyType;
        setValue1(totalStay);
        setvalue2(statementCredit);
        setValue3(totalAmountInCurrency);
        break;
      }
      case variationProps.isNightsVariation: {
        const newNightsTotal =
          variationProps.currentNights +
          (variationProps.mathValue ? parseInt(variationProps.mathValue.replace(/,/g, ''), 10) : 0);
        setValue1(convertToCommaSeparatedNumber(variationProps.currentNights));
        setvalue2(variationProps.mathValue);
        setValue3(convertToCommaSeparatedNumber(newNightsTotal));
        break;
      }
      default:
        break;
    }
  }, [variationProps]);

  if ((isTargetContentError && String(displayEvergreenContent) === 'false') || String(isHide) === 'true') {
    // here we are returning null to avoid rendering the component and performance issues
    // becoz we noticed that its triggering interact calls for tracking events even when the component is hidden, thats why we added this check
    return null;
  }

  const trackingPropertiesDynamic = getTrackingPropertiesDynamic(
    trackingProperties,
    resolvedMetaData,
    props,
    datalayer?.['mr_prof_authentication_state'] === 'authenticated',
    isTargetContentError,
    mboxParameter
  );

  const imageAlt = image?.altText;

  return (
    <StyledMerchandisingCoBrandCard
      data-testid="o-merch-MerchandisingCoBrandCard"
      data-component-name="o-merch-MerchandisingCoBrandCard"
      className={clsx(
        'merchandising-cobrand-card',
        't-background-color standard',
        styleClass,
        !isTargetContentLoaded && 'skeleton-loader loading',
        isSquareCard ? 'square-card' : 'tall-card',
        {
          'currency-card': variationDetails.isCurrencyVariation,
          'nights-card': variationDetails.isNightsVariation,
          'points-card': variationDetails.isPointsVariation,
        }
      )}
      $isVariationsAvailable={variationDetails.isVariationsAvailable}
    >
      {isTargetContentLoaded && (
        <Image
          customClass={clsx('merchandising-cobrand-card__image')}
          altText={imageAlt}
          renditions={image?.renditions}
          dynamic={image?.dynamic}
          defaultImageURL={image?.assetPath}
        />
      )}
      <div className="merchandising-cobrand-card__content">
        <MarkDownText
          textContent={title}
          className={clsx(
            'm-0',
            (variationDetails.isVariationsAvailable && 'merchandising-cobrand-card__title') ||
            'merchandising-cobrand-card__title-merch'
          )}
          variation={headingType.subtitle}
          fontSize={size.extraLarge}
          fallback={
            <Heading
              customClass={clsx(
                'm-0',
                (variationDetails.isVariationsAvailable && 'merchandising-cobrand-card__title') ||
                'merchandising-cobrand-card__title-merch'
              )}
              variation={headingType.subtitle}
              titleText={title}
              fontSize={size.extraLarge}
              element={tags.paragraph}
              dangerouslySet={title}
            />
          }
        />
        {description && (
          <MarkDownText
            textContent={description}
            className="t-font-s m-0"
            variation={headingType.body}
            fontSize={size.small}
            fallback={<p className="t-font-s m-0">{description}</p>}
          />
        )}
        {variationDetails.isVariationsAvailable && !isSquareCard && (
          <div className="merchandising-cobrand-card__variations">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="t-font-alt-s">{mathLabel1}</span>
              <span className="t-font-s">{value1}</span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="t-font-alt-s">{mathLabel2}</span>
              <span className="t-font-s">{value2}</span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="t-font-s merchandising-cobrand-card__variation-label">{mathLabel3}</span>
              <span className="t-font-s merchandising-cobrand-card__variations-value">{value3}</span>
            </div>
          </div>
        )}
      </div>
      <div className="merchandising-cobrand-card__footer">
        {isTargetContentLoaded && callToActionText1 && (
          <Button
            className={clsx('m-button-secondary', {
              'm-button-external': openInNewTab,
            })}
            href={destinationUrl1}
            isLink={true}
            target={openInNewTab === true ? '_blank' : '_self'}
            trackingProperties={trackingPropertiesDynamic}
            rel={`${openInNewTab ? 'noopener noreferrer' : ''}`}
            ref={buttonRef}
            buttonCopy={callToActionText1}
            linkAriaLabelOpenNewWindow={openInNewWindowAriaLabel}
            data-is-click-track={trackingProperties?.clickTrack}
          />
        )}
        {legalLong && variationDetails.isVariationsAvailable && !isSquareCard && (
          <p className="m-0 t-font-alt-xs">{legalLong}</p>
        )}
      </div>
    </StyledMerchandisingCoBrandCard>
  );
};

//TODO: The below code can be uncommented and used once the corresponding AEM component is created.
/*
const MerchandisingCoBrandCardConfig = {
  emptyLabel: 'MerchandisingCoBrandCard',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/merchcobrandcard`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MerchandisingCoBrandCard = (props: any) => {
  return (
    <EditableComponent
      config={MerchandisingCoBrandCardConfig}
      {...props}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children={<MerchandisingCoBrandCardComponent {...({} as any)} />}
    />
  );
};
*/
